import React, { useState, useEffect, useRef } from 'react';
import styles from './DonateModal.module.css';

const WORKER_URL = process.env.REACT_APP_PUSH_WORKER_URL;
const PRESETS = [3, 5, 10, 25];

function DonateModal({ onClose }) {
  const [selected, setSelected] = useState(5);
  const [custom, setCustom]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const inputRef = useRef(null);

  const amount  = custom !== '' ? parseFloat(custom) : selected;
  const isValid = !isNaN(amount) && amount >= 1;

  // Close on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleDonate() {
    if (!isValid || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${WORKER_URL}/donate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          successUrl: `${window.location.origin}/?donated=1`,
          cancelUrl: window.location.href,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Support GameDrop">
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>

        <div className={styles.heartIcon}>
          <HeartIcon />
        </div>

        <h2 className={styles.title}>Support GameDrop</h2>
        <p className={styles.sub}>Help keep free game tracking running for everyone.</p>

        <div className={styles.presets}>
          {PRESETS.map(p => (
            <button
              key={p}
              className={`${styles.preset} ${custom === '' && selected === p ? styles.presetActive : ''}`}
              onClick={() => { setSelected(p); setCustom(''); }}
            >
              ${p}
            </button>
          ))}
        </div>

        <div className={styles.customWrap}>
          <span className={styles.currency}>$</span>
          <input
            ref={inputRef}
            className={styles.customInput}
            type="number"
            min="1"
            step="1"
            placeholder="Or enter any amount"
            value={custom}
            onChange={e => setCustom(e.target.value)}
          />
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <button
          className={styles.donateBtn}
          onClick={handleDonate}
          disabled={!isValid || loading}
        >
          {loading
            ? <span className={styles.spinner} />
            : `Donate $${isValid ? (Number.isInteger(amount) ? amount : amount.toFixed(2)) : '—'}`
          }
        </button>

        <p className={styles.secure}>
          <LockIcon /> Secure payment via Stripe
        </p>
      </div>
    </div>
  );
}

function HeartIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

export default DonateModal;
